import { getNodesToDelete } from "@features/builder/graph/contextMenu/ContextNodeMenu/getNodesToDelete"
import { duplicateNodes } from "@features/builder/graph/contextMenu/ContextNodeMenu/duplicateNodes"
import { useSelectCurentNode } from "@features/builder/graph/contextMenu/ContextNodeMenu/useSelectCurrentNode"
import {
  nodesToDuplicateAtom,
  useCopyPaste,
} from "@features/builder/graph/contextMenu/useCopyPaste"
import { recalculateParentGroupsSizes, sortNodes } from "@features/builder/graph/helpers"
import { AppNode, EdgeData, NodeType } from "@features/builder/graph/types"
import { getRelativePosition } from "@features/builder/helpers/nodes"
import { KeyBinding } from "@features/keymap/KeyBinding"
import { formatModifierKey } from "@features/keymap/keyShortcutFormatter"
import {
  AddBoxOutlined,
  AddCircleOutline,
  CancelOutlined,
  ContentCopy,
  ContentPaste,
  ControlPointDuplicate,
  DeleteOutline,
  TimelineOutlined,
} from "@mui/icons-material"
import {
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Typography,
} from "@mui/material"
import { nextDrawerNodeIdAtom } from "@features/builder/graph/graphAtoms"
import configuration from "@utils/configuration"
import { Edge, InternalNode, useNodes, useReactFlow, useStoreApi } from "@xyflow/react"
import { useSetAtom } from "jotai"
import { createNodeInGroup } from "utils/node"
import { useMemo, useRef } from "react"
import { useElementAdjustedPosition } from "@hooks/useElementAdjustedPosition"
import { shortcuts } from "@features/keymap/shortcuts"
import useLockedNodesIds from "@features/builder/hooks/useLockedNodesIds"

export type ContextNodeMenuProps = {
  id: string
  x: number
  y: number
  onCloseClick: () => void
  onOpenEdgesDialog?: () => void
}

export default function ContextNodeMenu({
  id,
  x,
  y,
  onCloseClick,
  onOpenEdgesDialog,
}: ContextNodeMenuProps) {
  useSelectCurentNode(id)

  const reactFlowInstance = useReactFlow<AppNode, Edge<EdgeData>>()
  const allNodes = useNodes<AppNode>()
  const lockedNodesIds = useLockedNodesIds()
  const { getNode, setNodes, getNodes, screenToFlowPosition, getInternalNode, deleteElements } =
    reactFlowInstance

  const flowClickPosition = screenToFlowPosition({ x, y })
  const node = getNode(id)
  const store = useStoreApi<AppNode>()
  const setNextDrawerNodeId = useSetAtom(nextDrawerNodeIdAtom)
  const setNodesToDuplicate = useSetAtom(nodesToDuplicateAtom)

  const isDeleteDisabled = useMemo(() => {
    const nodesToDelete = getNodesToDelete(allNodes)

    return nodesToDelete.some((node) => lockedNodesIds.includes(node.id))
  }, [allNodes, lockedNodesIds])

  const menuRef = useRef<HTMLDivElement>(null)
  const menuPosition = useElementAdjustedPosition({ ref: menuRef, x, y })

  const { handleCopyNodes, handlePasteNodes } = useCopyPaste(onCloseClick)

  const handleDuplicateNode = () => {
    const allSourceNodes: InternalNode<AppNode>[] = []

    for (const node of getNodes()) {
      const internalNode = getInternalNode(node.id)
      if (internalNode) allSourceNodes.push(internalNode)
    }
    const { nodes, oldToNewIdMap } = duplicateNodes({
      allSourceNodes,
      allTargetNodes: getNodes(),
      underCursorNodeId: id,
      reactFlowInstance,
    })
    setNodesToDuplicate({ nodes, oldToNewIdMap })
    onCloseClick()
  }

  const handleDeleteNode = () => {
    const allNodes = getNodes()
    const nodesToDelete = getNodesToDelete(allNodes)
    deleteElements({ nodes: nodesToDelete })

    onCloseClick()
  }

  const handleAddNodeToGroup = (type: NodeType) => {
    if (node?.type !== NodeType.Group) {
      onCloseClick()
      return
    }

    const addGroup = type === NodeType.Group
    const newNodeType = addGroup ? NodeType.Group : NodeType.Empty

    const internalNode = getInternalNode(node.id)

    if (!internalNode) {
      onCloseClick()
      return
    }

    let updatedNodes = getNodes()

    const newNodePosition = getRelativePosition(
      flowClickPosition,
      internalNode.internals.positionAbsolute,
    )
    const newNode = createNodeInGroup(updatedNodes, node, newNodeType, newNodePosition)
    updatedNodes = recalculateParentGroupsSizes(newNode, [...updatedNodes, newNode])

    setNodes(sortNodes(updatedNodes))

    if (!addGroup) {
      setTimeout(() => {
        store.getState().resetSelectedElements()
        store.getState().addSelectedNodes([newNode.id])
        setNextDrawerNodeId(newNode.id)
      })
    }

    onCloseClick()
  }

  const handlePaste = () => {
    handlePasteNodes(flowClickPosition)
    onCloseClick()
  }

  const handleCopy = () => {
    handleCopyNodes()
    onCloseClick()
  }

  return (
    <Paper sx={{ position: "fixed", zIndex: 10, ...menuPosition }} ref={menuRef}>
      <MenuList dense>
        {configuration.IS_BUILD_NODE_ID_LABEL_ENABLED && (
          <>
            <p style={{ margin: "0.5em" }}>
              <small>Node: {node?.data.label || id}</small>
            </p>
            <Divider />
          </>
        )}
        {node?.type === NodeType.Group && (
          <>
            <MenuItem
              onClick={() => handleAddNodeToGroup(NodeType.Empty)}
              data-testid="node-menu-add-node-option"
            >
              <ListItemIcon>
                <AddCircleOutline fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add node</ListItemText>
            </MenuItem>

            <MenuItem
              onClick={() => handleAddNodeToGroup(NodeType.Group)}
              data-testid="node-menu-add-group-option"
            >
              <ListItemIcon>
                <AddBoxOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add group</ListItemText>
            </MenuItem>
            <Divider />
          </>
        )}

        <MenuItem onClick={handleCopy} data-testid="node-menu-copy-option">
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("ctrl")}C
          </Typography>
        </MenuItem>

        <MenuItem onClick={handlePaste} data-testid="node-menu-paste-option">
          <ListItemIcon>
            <ContentPaste fontSize="small" />
          </ListItemIcon>
          <ListItemText>Paste</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("ctrl")}V
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={onOpenEdgesDialog} data-testid="pane-menu-create-edges-option">
          <ListItemIcon>
            <TimelineOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create edges</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("ctrl")}E
          </Typography>
        </MenuItem>

        <MenuItem onClick={handleDuplicateNode} data-testid="node-menu-duplicate-option">
          <ListItemIcon>
            <ControlPointDuplicate fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={handleDeleteNode}
          disabled={isDeleteDisabled}
          data-testid="node-menu-delete-option"
        >
          <ListItemIcon>
            <DeleteOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            DEL
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={onCloseClick} data-testid="node-menu-close-option">
          <ListItemIcon>
            <CancelOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            ESC
          </Typography>
          <KeyBinding bind={shortcuts["close-node-menu"]} onKeyDown={onCloseClick} />
        </MenuItem>
      </MenuList>
    </Paper>
  )
}
